import React, { useEffect, useState } from 'react';
import { fetchMetadata, patchMetadata } from '../api/adminApi';
import { IconTrash, IconCreditCard, IconTruck, IconMapPin, IconCheck } from '../components/Icons';
import { showToast } from '../lib/toast';

const MANAGED_KEYS = new Set([
  'isCodEnabled',
  'isOnlineEnabled',
  'maxDeliveryDistanceKm',
  'maxDeliveryDistance',
  'MAX_DELIVERY_DISTANCE_KM',
  'maxDeliveryRadiusKm',
]);

const PRESET_DISTANCES = [5, 10, 15, 20, 25, 30, 40, 50];

function uid() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function getZoneDetails(km) {
  if (km <= 8) {
    return {
      label: '⚡ Nearby Delivery Zone',
      desc: 'Deliveries to nearby areas (around 10 to 25 mins).',
      badgeClass: 'bg-success-subtle text-success border border-success-subtle',
    };
  }
  if (km <= 20) {
    return {
      label: '🛵 City Delivery Zone',
      desc: 'Deliveries across the city (around 25 to 45 mins).',
      badgeClass: 'bg-primary-subtle text-primary border border-primary-subtle',
    };
  }
  if (km <= 35) {
    return {
      label: '🚚 Extended Delivery Zone',
      desc: 'Deliveries to outer areas and outskirts (around 45 to 60 mins).',
      badgeClass: 'bg-warning-subtle text-warning-emphasis border border-warning-subtle',
    };
  }
  return {
    label: '🚩 Maximum Distance Zone',
    desc: 'Long-distance deliveries up to 50 km.',
    badgeClass: 'bg-info-subtle text-info-emphasis border border-info-subtle',
  };
}

export default function Metadata() {
  const [rows, setRows] = useState([]);
  const [isCodEnabled, setIsCodEnabled] = useState(true);
  const [isOnlineEnabled, setIsOnlineEnabled] = useState(true);
  const [maxDeliveryDistanceKm, setMaxDeliveryDistanceKm] = useState(25);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const res = await fetchMetadata();
        const data = res ?? {};

        // Extract payment toggles (default to true if undefined)
        const codVal =
          data.isCodEnabled !== undefined
            ? data.isCodEnabled === true || data.isCodEnabled === 'true'
            : true;
        const onlineVal =
          data.isOnlineEnabled !== undefined
            ? data.isOnlineEnabled === true || data.isOnlineEnabled === 'true'
            : true;

        // Extract max delivery distance (default to 25 km, range 2 to 50 km)
        const rawDist =
          data.maxDeliveryDistanceKm ??
          data.maxDeliveryDistance ??
          data.MAX_DELIVERY_DISTANCE_KM ??
          data.maxDeliveryRadiusKm;
        const parsedDist = Number(rawDist);
        const resolvedDist =
          Number.isFinite(parsedDist) && parsedDist >= 2
            ? Math.min(50, Math.max(2, Math.round(parsedDist * 10) / 10))
            : 25;

        if (mounted) {
          setIsCodEnabled(codVal);
          setIsOnlineEnabled(onlineVal);
          setMaxDeliveryDistanceKm(resolvedDist);
        }

        // Filter out managed fields from custom key-value rows
        const items = Object.keys(data)
          .filter((k) => !MANAGED_KEYS.has(k))
          .map((k) => ({ id: uid(), key: k, value: data[k] }));

        if (mounted) {
          setRows(items.length ? items : [{ id: uid(), key: '', value: '' }]);
        }
      } catch (err) {
        if (mounted) {
          setIsCodEnabled(true);
          setIsOnlineEnabled(true);
          setMaxDeliveryDistanceKm(25);
          setRows([{ id: uid(), key: '', value: '' }]);
        }
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, []);

  function addRow() {
    setRows((s) => [...s, { id: uid(), key: '', value: '' }]);
  }

  function removeRow(id) {
    setRows((s) => s.filter((r) => r.id !== id));
  }

  function updateRow(id, field, val) {
    setRows((s) => s.map((r) => (r.id === id ? { ...r, [field]: val } : r)));
  }

  function handleDistanceChange(val) {
    const num = Number(val);
    if (!Number.isFinite(num)) return;
    const clamped = Math.min(50, Math.max(2, Math.round(num * 10) / 10));
    setMaxDeliveryDistanceKm(clamped);
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const payload = {
        isCodEnabled,
        isOnlineEnabled,
        maxDeliveryDistanceKm: Number(maxDeliveryDistanceKm),
      };
      for (const r of rows) {
        const k = (r.key ?? '').toString().trim();
        if (!k || MANAGED_KEYS.has(k)) continue;
        payload[k] = r.value ?? '';
      }
      await patchMetadata(payload);
      showToast('Settings saved successfully', 'success');

      // reload
      const res = await fetchMetadata();
      const data = res ?? {};

      const codVal =
        data.isCodEnabled !== undefined
          ? data.isCodEnabled === true || data.isCodEnabled === 'true'
          : true;
      const onlineVal =
        data.isOnlineEnabled !== undefined
          ? data.isOnlineEnabled === true || data.isOnlineEnabled === 'true'
          : true;

      const rawDist =
        data.maxDeliveryDistanceKm ??
        data.maxDeliveryDistance ??
        data.MAX_DELIVERY_DISTANCE_KM ??
        data.maxDeliveryRadiusKm;
      const parsedDist = Number(rawDist);
      const resolvedDist =
        Number.isFinite(parsedDist) && parsedDist >= 2
          ? Math.min(50, Math.max(2, Math.round(parsedDist * 10) / 10))
          : 25;

      setIsCodEnabled(codVal);
      setIsOnlineEnabled(onlineVal);
      setMaxDeliveryDistanceKm(resolvedDist);

      const items = Object.keys(data)
        .filter((k) => !MANAGED_KEYS.has(k))
        .map((k) => ({ id: uid(), key: k, value: data[k] }));
      setRows(items.length ? items : [{ id: uid(), key: '', value: '' }]);
    } catch (err) {
      setError(err?.message || String(err));
      showToast('Failed to save settings', 'danger');
    } finally {
      setSaving(false);
    }
  }

  const sliderPercent = ((maxDeliveryDistanceKm - 2) / (50 - 2)) * 100;
  const zone = getZoneDetails(maxDeliveryDistanceKm);

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h2 className="h6 mb-0">Metadata & System Settings</h2>
          <span className="text-secondary small">
            Configure delivery radius boundaries, checkout methods, and app metadata.
          </span>
        </div>
        <div>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary me-2"
            onClick={addRow}
            title="Add custom row"
          >
            + Add Row
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}

      {/* 1. Max Delivery Distance Range Slider Card */}
      <div
        className="card mb-4 border-0 shadow-sm overflow-hidden"
        style={{
          background: 'linear-gradient(145deg, #fffefc 0%, #fdf9f5 100%)',
          border: '1px solid rgba(232, 106, 36, 0.12)',
        }}
      >
        <div className="card-body p-4">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
            <div className="d-flex align-items-center gap-3">
              <div
                className="p-2 rounded-3 text-primary d-inline-flex align-items-center justify-content-center"
                style={{ background: 'rgba(232, 106, 36, 0.12)' }}
              >
                <IconTruck size={24} />
              </div>
              <div>
                <h3
                  className="h5 mb-0 text-dark"
                  style={{ fontFamily: 'var(--font-display)', fontWeight: 600 }}
                >
                  Maximum Delivery Distance Range
                </h3>
                <span className="text-secondary small">
                  Set how far your riders will deliver food from the cafe.
                </span>
              </div>
            </div>

            {/* Current Range Badge */}
            <div className="d-flex align-items-center gap-2">
              <span className={`badge px-3 py-2 rounded-pill fw-semibold ${zone.badgeClass}`}>
                {zone.label}
              </span>
              <div
                className="px-3 py-2 rounded-3 fw-bold text-white d-inline-flex align-items-baseline gap-1 shadow-sm"
                style={{ background: 'linear-gradient(135deg, #e86a24 0%, #cf5c1c 100%)', minWidth: '100px', justifyContent: 'center' }}
              >
                <span style={{ fontSize: '1.25rem' }}>{maxDeliveryDistanceKm}</span>
                <span style={{ fontSize: '0.82rem', opacity: 0.9 }}>km</span>
              </div>
            </div>
          </div>

          <p className="text-secondary small mb-4">
            Customers located within <strong>{maxDeliveryDistanceKm} km</strong> of the cafe can place delivery orders. Any customer address farther than <strong>{maxDeliveryDistanceKm} km</strong> will see that delivery is currently not available to their location.
          </p>

          {/* Slider Controls Container */}
          <div className="p-3 p-md-4 rounded-3 bg-white border shadow-xs mb-3">
            <div className="d-flex align-items-center justify-content-between mb-1">
              <span className="fw-semibold text-dark small d-flex align-items-center gap-1">
                <IconMapPin size={16} className="text-primary" /> Delivery Area Slider
              </span>
              <span className="text-muted small">
                Range: <strong>2 km</strong> (Minimum) to <strong>50 km</strong> (Maximum)
              </span>
            </div>

            {/* Range Input Slider */}
            <div className="py-2">
              <input
                type="range"
                className="delivery-range-slider"
                min="2"
                max="50"
                step="0.5"
                value={maxDeliveryDistanceKm}
                onChange={(e) => handleDistanceChange(e.target.value)}
                style={{
                  background: `linear-gradient(to right, #e86a24 0%, #e86a24 ${sliderPercent}%, #e7e2dc ${sliderPercent}%, #e7e2dc 100%)`,
                }}
                aria-label="Maximum delivery distance in kilometers"
              />
            </div>

            {/* Slider Scale Ticks */}
            <div className="d-flex justify-content-between text-muted small px-1 mb-3" style={{ fontSize: '0.75rem' }}>
              <span role="button" onClick={() => handleDistanceChange(2)} className="user-select-none hover-primary">
                2 km <span className="text-secondary opacity-75">(Min)</span>
              </span>
              <span role="button" onClick={() => handleDistanceChange(10)} className="user-select-none d-none d-sm-inline">
                10 km
              </span>
              <span role="button" onClick={() => handleDistanceChange(20)} className="user-select-none">
                20 km
              </span>
              <span role="button" onClick={() => handleDistanceChange(30)} className="user-select-none">
                30 km
              </span>
              <span role="button" onClick={() => handleDistanceChange(40)} className="user-select-none d-none d-sm-inline">
                40 km
              </span>
              <span role="button" onClick={() => handleDistanceChange(50)} className="user-select-none hover-primary">
                50 km <span className="text-secondary opacity-75">(Max)</span>
              </span>
            </div>

            <hr className="my-3 opacity-10" />

            {/* Quick Presets and Manual Stepper */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div className="d-flex flex-wrap align-items-center gap-2">
                <span className="text-secondary small fw-medium me-1">Quick Select:</span>
                {PRESET_DISTANCES.map((km) => (
                  <button
                    key={km}
                    type="button"
                    className={`delivery-preset-btn ${maxDeliveryDistanceKm === km ? 'active' : ''}`}
                    onClick={() => handleDistanceChange(km)}
                  >
                    {km} km
                  </button>
                ))}
              </div>

              {/* Manual Input / Stepper */}
              <div className="d-flex align-items-center gap-2">
                <span className="text-secondary small fw-medium">Type Value:</span>
                <div className="input-group input-group-sm" style={{ width: '135px' }}>
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-2"
                    onClick={() => handleDistanceChange(maxDeliveryDistanceKm - 1)}
                    disabled={maxDeliveryDistanceKm <= 2}
                    title="Decrease by 1 km"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    className="form-control text-center fw-semibold"
                    min="2"
                    max="50"
                    step="0.5"
                    value={maxDeliveryDistanceKm}
                    onChange={(e) => handleDistanceChange(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-2"
                    onClick={() => handleDistanceChange(maxDeliveryDistanceKm + 1)}
                    disabled={maxDeliveryDistanceKm >= 50}
                    title="Increase by 1 km"
                  >
                    +
                  </button>
                </div>
                <span className="text-muted small">km</span>
              </div>
            </div>
          </div>

          {/* Rule Impact Explainer Box */}
          <div
            className="p-3 rounded-3 d-flex flex-wrap align-items-center justify-content-between gap-2"
            style={{ background: 'rgba(255, 255, 255, 0.75)', border: '1px dashed rgba(232, 106, 36, 0.25)' }}
          >
            <div className="d-flex align-items-center gap-2">
              <div className="text-success d-flex align-items-center">
                <IconCheck size={18} />
              </div>
              <span className="small text-dark">
                <strong>Current Coverage:</strong> {zone.desc}
              </span>
            </div>
            <div className="small text-muted">
              Delivery limit set to <strong>{maxDeliveryDistanceKm} km</strong>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Fulfillment & Payment methods card */}
      <div
        className="card mb-4 border-0 shadow-sm overflow-hidden"
        style={{ background: 'linear-gradient(145deg, #fffefc 0%, #fbf8f4 100%)' }}
      >
        <div className="card-body p-4">
          <div className="d-flex align-items-center gap-2 mb-3">
            <div className="bg-primary-subtle p-2 rounded-3 text-primary d-inline-flex">
              <IconCreditCard size={20} />
            </div>
            <h3
              className="h5 mb-0 text-dark"
              style={{ fontFamily: 'var(--font-display)', fontWeight: 600 }}
            >
              Payment Method Controls
            </h3>
          </div>
          <p className="text-secondary small mb-4">
            Enable or disable checkout payment gateways. Disabled payment methods will not be visible to
            customers on the checkout screen in the customer app.
          </p>

          <div className="row g-4">
            <div className="col-md-6">
              <div className="p-3 rounded-3 border bg-white d-flex align-items-center justify-content-between hover-card-effect shadow-xs">
                <div className="pe-3">
                  <div className="fw-semibold text-dark mb-1">Cash on Delivery (COD)</div>
                  <div className="text-muted small">
                    Allow customers to pay with cash upon order delivery.
                  </div>
                </div>
                <div className="form-check form-switch admin-category-switch mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="isCodEnabledSwitch"
                    checked={isCodEnabled}
                    onChange={(e) => setIsCodEnabled(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="p-3 rounded-3 border bg-white d-flex align-items-center justify-content-between hover-card-effect shadow-xs">
                <div className="pe-3">
                  <div className="fw-semibold text-dark mb-1">Online Payments</div>
                  <div className="text-muted small">
                    Accept instant credit/debit card, UPI, and netbanking payments via Razorpay.
                  </div>
                </div>
                <div className="form-check form-switch admin-category-switch mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="isOnlineEnabledSwitch"
                    checked={isOnlineEnabled}
                    onChange={(e) => setIsOnlineEnabled(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. Custom Metadata Grid */}
      <div className="card">
        <div className="card-header bg-white border-bottom py-3 px-4">
          <h4 className="h6 mb-0 text-dark" style={{ fontWeight: 600 }}>
            Custom Metadata Grid
          </h4>
          <span className="text-muted small">
            Configure arbitrary key-value parameters for application metadata.
          </span>
        </div>
        <div className="card-body p-3">
          <div className="table-responsive">
            <table className="table table-borderless mb-0">
              <thead>
                <tr>
                  <th style={{ width: '40%' }}>Key</th>
                  <th>Value</th>
                  <th style={{ width: '48px' }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <input
                        className="form-control form-control-sm"
                        value={r.key}
                        onChange={(e) => updateRow(r.id, 'key', e.target.value)}
                        placeholder="key"
                      />
                    </td>
                    <td>
                      <input
                        className="form-control form-control-sm"
                        value={r.value}
                        onChange={(e) => updateRow(r.id, 'value', e.target.value)}
                        placeholder="value"
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-light"
                        onClick={() => removeRow(r.id)}
                        title="Delete"
                      >
                        <IconTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
